import type { CSSProperties, SyntheticEvent } from "react";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";

import { hexOf, FONT_STACKS, type LinkStyle } from "../domain/canvas-model";

function faviconUrl(url: string): string | null {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=64`;
  } catch {
    return null;
  }
}

/**
 * Shared LINK renderer (editor + public Grid, pixel-identical). Presets: TEXT
 * (styled link), BUTTON (filled CTA), OUTLINE (bordered), CARD (label + subtitle
 * block). Optional leading icon: ARROW (open ↗) or FAVICON (site logo from URL).
 * Rendered at natural size; the caller wraps it in <FitToBox> so it scales to the
 * slot. On the Grid the whole block is wrapped by the caller's <a>.
 */
export function LinkRender({
  label, subtitle, url, style, scale, interactive,
}: {
  label: string; subtitle?: string; url?: string; style: LinkStyle; scale: number; interactive: boolean;
}) {
  const px = (n: number) => Math.max(0, n * scale);
  const s = style;
  const fontSize = Math.max(1, px(s.font_size));
  const subFont = Math.max(1, px(s.font_size * 0.72));
  const iconSize = Math.max(4, px(s.font_size * 1.05));
  const pad = px(s.padding);
  const radius = s.radius === "PILL" ? 9999 : s.radius === "ROUNDED" ? 12 * scale : s.radius === "SLIGHT" ? 5 * scale : 0;
  const chrome = s.bg_enabled || s.border_enabled;
  const bg = s.bg_enabled ? hexOf(s.bg_color, "#2f7bff") : "transparent";
  const border = s.border_enabled ? `${Math.max(1, 1.5 * scale)}px solid ${hexOf(s.border_color, "#2f7bff")}` : "none";
  const labelColor = hexOf(s.label_color, "#2f7bff");
  const subColor = hexOf(s.subtitle_color, "#c8d0dc");
  const font = FONT_STACKS[s.font] ?? FONT_STACKS.GRID_SANS;
  const alignItems = s.align === "START" ? "flex-start" : s.align === "END" ? "flex-end" : "center";
  const fav = s.icon === "FAVICON" && url ? faviconUrl(url) : null;

  const wrap: CSSProperties = {
    display: "inline-flex", flexDirection: "column", alignItems, gap: px(4),
    background: bg, border, borderRadius: radius, padding: chrome ? `${px(8)}px ${pad}px` : 0,
    boxSizing: "border-box", textDecoration: "none", maxWidth: "100%",
  };
  return (
    <span className={interactive && s.hover !== "NONE" ? `social-item hv-${s.hover.toLowerCase()}` : "social-item"} style={wrap}>
      <span style={{ display: "inline-flex", flexDirection: "row", alignItems: "center", gap: px(6), color: labelColor }}>
        {s.icon === "ARROW" && <FaArrowUpRightFromSquare size={iconSize} color={labelColor} style={{ flexShrink: 0 }} />}
        {fav && <img src={fav} alt="" width={iconSize} height={iconSize} style={{ flexShrink: 0, borderRadius: 2 * scale, display: "block" }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />}
        <span style={{ fontFamily: font, fontSize, fontWeight: 600, color: labelColor, lineHeight: 1.15, textDecoration: s.underline ? "underline" : "none", whiteSpace: "nowrap" }}>{label}</span>
      </span>
      {subtitle && subtitle.trim() && (
        <span style={{ fontFamily: font, fontSize: subFont, color: subColor, lineHeight: 1.2, whiteSpace: "nowrap" }}>{subtitle}</span>
      )}
    </span>
  );
}

export const stopLinkPan = (e: SyntheticEvent) => e.stopPropagation();
